/**
 * Documentos que o DONO do parceiro compartilha com a equipe dele (pacote D).
 *
 * `subir` e `remover` só funcionam para quem tem `portal:documentos_equipe:manage`
 * — o backend é quem decide (403). A tela esconde os botões, mas esconder botão
 * não é autorizar: quem chamar a API direto continua barrado.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { documentosEquipeApi } from "@/services/portalApi";
import type { DocumentoEquipeListResponse } from "@/types/portal";

const CHAVE = ["portal", "documentos-equipe"] as const;

export function useDocumentosEquipe(page = 1, pageSize = 20) {
  return useQuery<DocumentoEquipeListResponse>({
    queryKey: [...CHAVE, page, pageSize],
    queryFn: () => documentosEquipeApi.listar({ page, page_size: pageSize }),
  });
}

export function useSubirDocumentoEquipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { titulo: string; categoria?: string; arquivo: File }) =>
      documentosEquipeApi.subir(dados),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAVE });
    },
  });
}

export function useRemoverDocumentoEquipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) => documentosEquipeApi.remover(docId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAVE });
    },
  });
}

/**
 * Baixa o arquivo. A URL assinada é buscada na hora e usada imediatamente —
 * nunca guardada em estado, porque expira em minutos e um link vencido na tela
 * vira "o download não funciona" sem explicação.
 */
export function useBaixarDocumentoEquipe() {
  return useMutation({
    mutationFn: async (docId: string) => {
      const info = await documentosEquipeApi.obterDownload(docId);
      const link = document.createElement("a");
      link.href = info.url;
      link.download = info.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return info;
    },
  });
}
